import { OPCOES_FILTRO_DATA } from '@constants/Text'
import { FILTER_MODE } from '@modules/consolidator/constants/filter-mode'

const PREFERENCES_KEY = 'aawz_consolidator_preferences'
const VIEW_MODE_ESTRATEGICA = 'estrategica'
const VIEW_MODE_DESENQUADRAMENTO = 'desenquadramento'
const VALID_VIEW_MODES = new Set([
  VIEW_MODE_ESTRATEGICA,
  VIEW_MODE_DESENQUADRAMENTO,
])

const _readPreferences = () => {
  try {
    if (globalThis.window === undefined || !globalThis.window.localStorage) {
      return { viewMode: VIEW_MODE_ESTRATEGICA }
    }
    const raw = globalThis.window.localStorage.getItem(PREFERENCES_KEY)
    if (!raw) {
      return { viewMode: VIEW_MODE_ESTRATEGICA }
    }
    const parsed = JSON.parse(raw)
    const viewMode = VALID_VIEW_MODES.has(parsed && parsed.viewMode)
      ? parsed.viewMode
      : VIEW_MODE_ESTRATEGICA
    return { viewMode }
  } catch (error) {
    console.warn(
      '[consolidator] Falha ao ler preferências do localStorage:',
      error
    )
    return { viewMode: VIEW_MODE_ESTRATEGICA }
  }
}

export const PREFERENCES_STORAGE_KEY = PREFERENCES_KEY
export const VIEW_MODES = {
  ESTRATEGICA: VIEW_MODE_ESTRATEGICA,
  DESENQUADRAMENTO: VIEW_MODE_DESENQUADRAMENTO,
}

// Dimensão padrão do breakdown da carteira do grupo familiar. É `subclasse`, a
// mesma da carteira individual: o consultor abre a tela querendo ver a alocação,
// e "por membro" é o recorte que ele escolhe quando quer comparar as carteiras.
// `id_cliente` continua sendo a dimensão que alimenta a seção "Por Carteira".
const FAMILY_GROUP_BREAKDOWN_BY_SUBCLASSE = 'subclasse'

/**
 * Filtros padrão da carteira do grupo familiar.
 *
 * Fábrica, e não constante exportada: cada reset precisa receber arrays novos.
 * Compartilhar as referências faria a seleção de um grupo vazar para o próximo.
 *
 * As chaves são os nomes dos parâmetros da API — os mesmos que a classe de API
 * serializa e que `build-group-wallet-options-fields` usa como `key` de cada
 * campo. Sem camada de tradução entre UI e query string.
 */
export const buildDefaultFamilyGroupFilters = () => ({
  id_conta: [],
  id_cliente: [],
  group_by: FAMILY_GROUP_BREAKDOWN_BY_SUBCLASSE,
  // Mesma janela padrão da Carteira Atual e da Análise Portfólio do cliente.
  // Sem período o backend devolve a série desde o início, e a rentabilidade da
  // tela do grupo não batia com a do cliente só por causa do recorte.
  filtroData: OPCOES_FILTRO_DATA.ANO,
})

export const buildInitialState = () => ({
  preferences: _readPreferences(),
  consolidateExtracts: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  cotizacao: {
    loading: false,
    lastUpdate: null,
    data: {},
  },
  overview: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  overviewFilters: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  assetsLiquidity: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: [],
  },
  clientAssets: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  clientsDataOutdated: {
    data: false,
  },
  recotizacaoEmAndamento: {
    data: null,
  },
  recentlyLinkedAssetIds: [],
  searchAssets: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  versionClosing: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  backOfficeComparison: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  fullWallet: {
    lastUpdate: null,
    data: [],
    brokers: [],
    portfolios: [],
  },
  correlations: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: [],
  },
  riskMetrics: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: [],
  },
  InsightsIa: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: [],
  },
  investmentAccount: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  analisysAdvanced: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: [],
  },
  carteiraAtivos: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  investmentAccounts: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  simulatorResult: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: [],
  },
  walletFilters: {
    data: {
      modoFiltroCorretora: '',
      filtroCorretora: [],
      filtroData: '',
      lastValidFiltroData: '',
      investmentTypeFilter: '',
      dataInicio: null,
      dataFim: null,
      tipo: [],
      subtipo: [],
      classe: [],
      subclasse: [],
      setor: [],
      emissor: [],
      idConta: [],
      tags: [],
      instituicao: [],
      portfolio: [],
      userChangedFields: {},
    },
  },
  walletAnalysis: {
    loading: false,
    lastUpdate: null,
    data: null,
  },
  walletAnalysisOptionsFields: {
    loading: false,
    data: [],
  },
  statisticsPerAsset: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: {},
  },
  assetSeries: {
    lastUpdate: null,
    data: {},
  },
  assetSeriesBenchmarks: {
    lastUpdate: null,
    data: {},
  },
  portfolios: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  proventos: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: {},
  },
  splits: {
    loading: false,
    lastUpdate: null,
    clientId: null,
    data: {},
  },
  // Grupo familiar selecionado. Slot DEDICADO, nunca `user/currentClient`: o
  // contexto de cliente é único e global, e sobrescrevê-lo quebraria todas as
  // telas de carteira por cliente.
  currentFamilyGroup: {
    loading: false,
    lastUpdate: null,
    data: null,
  },
  walletSelectorMode: {
    loading: false,
    lastUpdate: null,
    data: FILTER_MODE.CLIENTES,
  },
  familyGroupWallet: {
    loading: false,
    lastUpdate: null,
    data: null,
  },
  familyGroupMemberBreakdown: {
    loading: false,
    lastUpdate: null,
    data: null,
  },
  familyGroupAssetStatistics: {
    loading: false,
    lastUpdate: null,
    data: null,
  },
  familyGroupFilters: {
    data: buildDefaultFamilyGroupFilters(),
  },
  familyGroupOptionsFields: {
    loading: false,
    lastUpdate: null,
    data: [],
  },
  // {{NEXT}}
})

export default buildInitialState()
